export interface ChatErrorInfo {
  message: string;
  details: string;
  /** Secondary muted line: what the user can DO about it. */
  hint?: string;
  /** Server-side reference id, when the error body carried one. */
  requestId?: string;
}

function stringifyError(error: unknown): string {
  if (error instanceof Error) {
    const extra = Object.entries(error as unknown as Record<string, unknown>)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value, null, 2)}`)
      .join('\n');
    return [
      `${error.name}: ${error.message}`,
      error.stack,
      extra,
    ].filter(Boolean).join('\n\n');
  }

  if (typeof error === 'string') return error;

  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

const MAX_MESSAGE_CHARS = 200;
const MAX_DETAILS_CHARS = 8_000;

function looksLikeHtml(body: string): boolean {
  const head = body.trimStart().slice(0, 512).toLowerCase();
  return (
    head.startsWith('<!doctype') ||
    head.startsWith('<html') ||
    head.includes('<html') ||
    head.includes('<head>') ||
    head.includes('<body')
  );
}

/** Pull an HTTP status out of the error body/fields if one is present. */
function extractStatus(raw: string): string | null {
  const fieldMatch = raw.match(/status(?:Code)?["']?\s*[:=]\s*["']?([1-5]\d\d)/i);
  if (fieldMatch) return fieldMatch[1];
  const bareMatch = raw.match(/\b([45]\d\d)\b/);
  return bareMatch ? bareMatch[1] : null;
}

/**
 * Reduce an HTML error page (or any oversized opaque body) to one concise
 * line. HTML pages: prefer <title> / first <h1>, else strip tags and take
 * the first meaningful text line. Everything is truncated to ~200 chars.
 */
function summarizeOpaqueBody(body: string): string {
  let text = body;
  if (looksLikeHtml(body)) {
    const title = body.match(/<title[^>]*>([\s\S]*?)<\/title>/i)?.[1]?.trim();
    const h1 = body.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i)?.[1]?.replace(/<[^>]+>/g, '').trim();
    text = title || h1 || body
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<[^>]+>/g, ' ');
  }
  const firstLine = text
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .find((line) => line.length > 0) ?? 'Unrecognized server response';
  return firstLine.length > MAX_MESSAGE_CHARS
    ? `${firstLine.slice(0, MAX_MESSAGE_CHARS - 1)}…`
    : firstLine;
}

/** Pull a request/reference id out of an error body if one is present. */
function extractRequestId(raw: string): string | undefined {
  const m = raw.match(/request[_-]?id["':\s]+["']?([A-Za-z0-9-]{8,})/i);
  return m?.[1];
}

export function formatChatError(error: unknown): ChatErrorInfo {
  let details = stringifyError(error);
  let msg = error instanceof Error ? error.message : details || 'Something went wrong';

  // The client retry loop wraps the real cause: "Failed after 3 attempts.
  // Last error: <cause>". Classify the CAUSE, not the wrapper.
  const wrapped = msg.match(/^Failed after \d+ attempts?\.\s*Last error:\s*([\s\S]+)$/i);
  if (wrapped) msg = wrapped[1].trim();

  // Non-stream error responses (proxy/CDN/Next error pages) surface the raw
  // body as the error message. Never render a whole HTML document (or a
  // megabyte of anything) in the alert — reduce to status + first
  // meaningful line, and cap the expandable details too.
  const isHtmlBody = looksLikeHtml(msg);
  const isOversizedBody = msg.length > 600;
  if (details.length > MAX_DETAILS_CHARS) {
    details = `${details.slice(0, MAX_DETAILS_CHARS)}\n… [truncated ${details.length - MAX_DETAILS_CHARS} chars]`;
  }
  if (isHtmlBody || isOversizedBody) {
    const status = extractStatus(msg) ?? extractStatus(details);
    const summary = summarizeOpaqueBody(msg);
    return {
      message: `The server returned an unexpected ${isHtmlBody ? 'HTML ' : ''}response${status ? ` (status ${status})` : ''}: ${summary}`,
      details,
    };
  }
  const lowerDetails = details.toLowerCase();
  const isProviderBillingError =
    (lowerDetails.includes('openrouter') &&
      (lowerDetails.includes('insufficient credits') ||
        lowerDetails.includes('key limit exceeded') ||
        lowerDetails.includes('statuscode: 402') ||
        lowerDetails.includes('status code: 402'))) ||
    (lowerDetails.includes('digitalocean') &&
      (lowerDetails.includes('insufficient credits') ||
        lowerDetails.includes('quota') ||
        lowerDetails.includes('billing')));
  const isVercelStepTimeout =
    /Vercel Runtime Timeout Error/i.test(msg) ||
    /Task timed out after 300/i.test(msg) ||
    /\/workflow\/v1\/step.*504/i.test(msg);

  if (/failed to fetch|networkerror|network error|fetch failed|load failed|err_(network|internet|connection)/i.test(msg)) {
    return {
      message: 'Connection lost while generating.',
      hint: 'Check your internet and retry — completed work is saved, the turn resumes where it stopped.',
      details,
    };
  }

  // JSON error bodies (e.g. {"error":"Internal Server Error","requestId":"…"})
  // — never render raw JSON as the headline; extract the reference id so it
  // can be reported/correlated.
  if (msg.trimStart().startsWith('{')) {
    try {
      const body = JSON.parse(msg) as Record<string, unknown>;
      const errText = typeof body.error === 'string' ? body.error : typeof body.message === 'string' ? body.message : '';
      const requestId = typeof body.requestId === 'string' ? body.requestId : extractRequestId(details);
      if (/internal server error/i.test(errText) || extractStatus(msg) === '500') {
        return {
          message: 'Something went wrong on our side.',
          hint: 'Retrying usually works. If it keeps failing, send us the reference ID below.',
          requestId,
          details,
        };
      }
      if (errText) {
        return { message: errText, requestId, details };
      }
    } catch { /* not JSON after all — fall through */ }
  }

  if (/platform overloaded|provider.{0,20}overloaded|overloaded\. please try again/i.test(msg)) {
    return {
      message: 'The AI provider is overloaded right now.',
      hint: 'This is on their side and usually passes in seconds — just retry.',
      details,
    };
  }
  if (isProviderBillingError) {
    return {
      message: 'The selected inference provider rejected the request because its account/key has no credits, quota, or billing access. This is not your Galexia credit balance; switch models or update the provider billing/key.',
      details,
    };
  }
  if (msg.includes('rate limit') || msg.includes('429')) {
    return { message: 'Too many requests. Please wait a moment and try again.', details };
  }
  if (isVercelStepTimeout) {
    return {
      message: 'Generation hit the 5-minute time limit. Completed tool outputs are saved. Retry to continue, or break the request into smaller parts.',
      details,
    };
  }
  if (msg.includes('timeout') || msg.includes('504')) {
    return { message: 'The generation timed out. Completed tool outputs are saved. Retry to continue.', details };
  }
  if (msg.includes('503')) {
    return { message: 'The server is temporarily unavailable. Please try again.', details };
  }
  if (msg.includes('NoSuchToolError') || msg.includes('No such tool') || msg.includes('Tool "') && msg.includes('" not found')) {
    return { message: 'A tool call failed because the requested tool was not available. Retry will continue with the corrected tool set.', details };
  }

  return { message: msg || 'Something went wrong. Please try again.', requestId: extractRequestId(details), details };
}
